import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const EmergencyContact = () => {
  const [isExpanded, setIsExpanded] = useState(false);

  const emergencyServices = [
    {
      title: "Network Outage",
      description: "Complete loss of connectivity affecting business operations",
      responseTime: "1-2 hours",
      icon: "WifiOff"
    },
    {
      title: "Infrastructure Failure",
      description: "Critical hardware failure requiring immediate replacement",
      responseTime: "2-4 hours",
      icon: "AlertTriangle"
    },
    {
      title: "Security Breach",
      description: "Network security compromise requiring immediate isolation",
      responseTime: "30-60 minutes",
      icon: "Shield"
    },
    {
      title: "Performance Crisis",
      description: "Severe performance degradation affecting productivity",
      responseTime: "1-3 hours",
      icon: "Zap"
    }
  ];

  const emergencyContacts = [
    {
      type: "Primary Emergency Line",
      number: "(555) 911-FIBER",
      description: "24/7 emergency response team",
      icon: "Phone"
    },
    {
      type: "Emergency Text/SMS",
      number: "01483 343 064",
      description: "For situations where calling isn\'t possible",
      icon: "MessageSquare"
    },
    {
      type: "Emergency Email",
      number: "admin@xtreemfibresolutions.co.uk",
      description: "Include \'EMERGENCY\' in subject line",
      icon: "Mail"
    }
  ];

  return (
    <div className="bg-gradient-to-br from-error/5 to-error/10 border-2 border-error/20 rounded-lg p-6">
      <div className="flex items-start space-x-4 mb-6">
        <div className="flex-shrink-0">
          <div className="w-12 h-12 bg-error rounded-full flex items-center justify-center">
            <Icon name="AlertTriangle" size={24} className="text-white" />
          </div>
        </div>
        <div className="flex-1">
          <h3 className="text-xl font-bold text-error mb-2">Emergency Support Available</h3>
          <p className="text-text-secondary mb-4">
            Experiencing a critical connectivity issue? Our emergency response team is available 24/7 
            to provide immediate assistance for business-critical infrastructure problems.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="destructive"
              size="lg"
              iconName="Phone"
              iconPosition="left"
              className="font-semibold"
            >
              Call Emergency: 01483 343 064
            </Button>
            <Button
              variant="outline"
              size="lg"
              iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
              iconPosition="right"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? "Hide Details" : "View Emergency Services"}
            </Button>
          </div>
        </div>
      </div>
      {/* Expandable Emergency Details */}
      <div className={`expandable-content ${isExpanded ? 'expanded' : ''}`}>
        <div className="space-y-6 pt-6 border-t border-error/20">
          {/* Emergency Services */}
          <div>
            <h4 className="font-semibold text-text-primary mb-4">Emergency Response Services</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {emergencyServices?.map((service, index) => (
                <div key={index} className="bg-white/80 border border-error/10 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Icon name={service?.icon} size={20} className="text-error mt-1" />
                    <div className="flex-1">
                      <h5 className="font-medium text-text-primary mb-1">{service?.title}</h5>
                      <p className="text-sm text-text-secondary mb-2">{service?.description}</p>
                      <div className="flex items-center space-x-2">
                        <Icon name="Clock" size={14} className="text-warning" />
                        <span className="text-sm font-medium text-warning">
                          Response: {service?.responseTime}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Emergency Contact Methods */}
          <div>
            <h4 className="font-semibold text-text-primary mb-4">Emergency Contact Methods</h4>
            <div className="space-y-3">
              {emergencyContacts?.map((contact, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-white/80 border border-error/10 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Icon name={contact?.icon} size={20} className="text-error" />
                    <div>
                      <h5 className="font-medium text-text-primary">{contact?.type}</h5>
                      <p className="text-sm text-text-secondary">{contact?.description}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-mono font-semibold text-error">{contact?.number}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Emergency Information */}
          <div className="bg-white/80 border border-error/10 rounded-lg p-4">
            <h5 className="font-semibold text-text-primary mb-3 flex items-center">
              <Icon name="Info" size={18} className="mr-2 text-primary" />
              What to Have Ready
            </h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Icon name="Check" size={14} className="text-accent" />
                  <span className="text-sm text-text-secondary">Company name and contact information</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Check" size={14} className="text-accent" />
                  <span className="text-sm text-text-secondary">Description of the problem</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Check" size={14} className="text-accent" />
                  <span className="text-sm text-text-secondary">Current network setup details</span>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Icon name="Check" size={14} className="text-accent" />
                  <span className="text-sm text-text-secondary">Business impact assessment</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Check" size={14} className="text-accent" />
                  <span className="text-sm text-text-secondary">Access requirements for technicians</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Check" size={14} className="text-accent" />
                  <span className="text-sm text-text-secondary">Preferred contact method for updates</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmergencyContact;